// 档2 — 豆包同声传译 AST 2.0 playground。跨语种实时口译：麦克风源语音 → 源语字幕(原文) +
// 目标语字幕(译文) + 目标语译音(s2s)。与 realtime/TTS 共用沉浸式云球，但驱动 AST v4 protobuf
// 协议(ws-ast-proto.js，整条 WS 消息即一个 protobuf message，无火山 v3 二进制头)。
//
// Flow: getWsToken → 连 ws-gateway(桥到 /api/v4/ast/v2/translate) → onopen 直接 StartSession(100)
//   → SessionStarted(150) → 麦克风 PCM16/16k → TaskRequest(200) 上行；下行 SourceSubtitle(650-652)
//   原文 + TranslationSubtitle(653-655) 译文 + TTSResponse(352,PCM24k) 译音 + Usage(154)。断开发 FinishSession。
//
// 计费：网关按墙钟分钟计(adapter durationSource='wallclock')，右上角据连接时长估算；Usage 事件的
//   模态 token / DurationMsec 仅作展示。

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import WsRealtimeOrb from './ws-realtime-orb.jsx';
import * as Ast from './ws-ast-proto.js';

const RATE_IN = 16000;   // 上行 PCM16 mono（源音频）
const RATE_OUT = 24000;  // 下行译音 PCM Float32 mono（target_audio pcm/24000，实测 32float，非 Int16）

// 目前 AST 主要支持中英互译；预留更多语向。
const LANGS = [
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
];
const langLabel = (c) => (LANGS.find((l) => l.code === c) || { label: c }).label;

const PHASE_LABEL = {
  idle: '未连接',
  connecting: '连接中…',
  ready: '准备就绪 · 开始说话即实时口译',
  listening: '聆听中…',
  speaking: '播报译文…',
};

// Float32 @inputRate → Int16 @RATE_IN(线性抽取)
function floatToPcm16(float32, inputRate) {
  const ratio = inputRate / RATE_IN;
  const outLen = Math.floor(float32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, float32[Math.floor(i * ratio)]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return new Uint8Array(out.buffer);
}
const isSecure = () =>
  typeof window !== 'undefined' &&
  (window.isSecureContext || /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname));

// 模块级（稳定标识，避免定义在 render 内导致每次更新整段重挂载 → 滚动位置被重置）。
// scrollRef 由父组件持有，配合父组件的 useEffect 在字幕增长时自动滚到底。
function SubtitlePanel({ title, segs, lang, muted, live, scrollRef }) {
  return (
    <div className="col" style={{ flex: 1, minWidth: 220, background: 'var(--bg-2, var(--panel-2))', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
      <div className="row between" style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)' }}>
        <span className="micro" style={{ color: muted ? 'var(--fg-3)' : 'var(--fg-1)' }}>{title}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-4)' }}>{langLabel(lang)}</span>
      </div>
      <div ref={scrollRef} className="col gap-2" style={{ padding: 12, minHeight: 200, maxHeight: 300, overflowY: 'auto' }}>
        {segs.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>{live ? '等待语音…' : '连接后开始'}</div>
        ) : segs.map((seg) => (
          <div key={seg.id} style={{ fontSize: muted ? 13.5 : 15, lineHeight: 1.55, color: seg.done ? (muted ? 'var(--fg-3)' : 'var(--fg-1)') : 'var(--fg-2)', overflowWrap: 'anywhere' }}>
            {seg.text || '…'}{!seg.done && <span className="mono" style={{ color: 'var(--fg-4)' }}>▋</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WsPlaygroundDoubaoAst({ api, endpoint }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/v1/ast';
  const perMinuteUsd = Number(wsBilling.perMinuteUsd) || 0;

  const [status, setStatus] = React.useState('idle'); // idle|connecting|ready|recording|closed|error
  const [phase, setPhase] = React.useState('idle');
  const [sourceLang, setSourceLang] = React.useState('zh');
  const [targetLang, setTargetLang] = React.useState('en');
  const [mode, setMode] = React.useState('s2s'); // s2s(带译音) | s2t(仅字幕)
  const [srcSegs, setSrcSegs] = React.useState([]); // 原文 [{id,text,done}]
  const [tgtSegs, setTgtSegs] = React.useState([]); // 译文
  const [usage, setUsage] = React.useState({ inputAudio: 0, outputText: 0, outputAudio: 0, durationMsec: 0 });
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const [errorMsg, setErrorMsg] = React.useState('');

  const wsRef = React.useRef(null);
  const statusRef = React.useRef('idle');
  const sidRef = React.useRef(null);
  const cidRef = React.useRef(null);
  const seqRef = React.useRef(0);
  const startedAtRef = React.useRef(0);
  const timerRef = React.useRef(null);
  const finishedRef = React.useRef(false);
  const segIdRef = React.useRef(0);
  // 音频
  const micCtxRef = React.useRef(null);
  const micStreamRef = React.useRef(null);
  const micNodeRef = React.useRef(null);
  const playCtxRef = React.useRef(null);
  const playAtRef = React.useRef(0);
  const playSourcesRef = React.useRef([]);
  const micAnalyserRef = React.useRef(null);
  const outAnalyserRef = React.useRef(null);
  // 字幕滚动容器：字幕增长时自动滚到底（跟随最新一段）
  const srcScrollRef = React.useRef(null);
  const tgtScrollRef = React.useRef(null);

  const setStatusBoth = (s) => { statusRef.current = s; setStatus(s); };

  React.useEffect(() => { const el = srcScrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [srcSegs]);
  React.useEffect(() => { const el = tgtScrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [tgtSegs]);
  const nextSeq = () => seqRef.current++;
  const newSegId = () => `s${segIdRef.current++}`;

  const teardownAudio = React.useCallback(() => {
    if (micNodeRef.current) { try { micNodeRef.current.disconnect(); } catch {} micNodeRef.current = null; }
    if (micCtxRef.current) { try { micCtxRef.current.close(); } catch {} micCtxRef.current = null; }
    if (micStreamRef.current) { try { micStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {} micStreamRef.current = null; }
    if (playCtxRef.current) { try { playCtxRef.current.close(); } catch {} playCtxRef.current = null; }
    micAnalyserRef.current = null; outAnalyserRef.current = null;
    playAtRef.current = 0; playSourcesRef.current = [];
  }, []);

  const stopTimer = React.useCallback(() => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } }, []);

  const gracefulFinish = React.useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && sidRef.current && !finishedRef.current) {
      finishedRef.current = true;
      try { ws.send(Ast.encodeFinish({ sessionId: sidRef.current, connectionId: cidRef.current, sequence: nextSeq() })); } catch {}
    }
  }, []);

  const teardown = React.useCallback((closeSocket = true) => {
    stopTimer();
    teardownAudio();
    if (closeSocket && wsRef.current) {
      gracefulFinish();
      try { wsRef.current.close(1000, 'client stop'); } catch {}
      wsRef.current = null;
    }
  }, [teardownAudio, gracefulFinish, stopTimer]);

  React.useEffect(() => () => teardown(true), [teardown]);

  // 播放一段 Float32 LE 24k PCM(无缝排队)。AST target pcm@24k 实测为 32float(非 Int16)。
  const playAudio = React.useCallback((bytes) => {
    try {
      if (!bytes || bytes.byteLength < 4) return;
      if (!playCtxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        playCtxRef.current = new AudioCtx();
        playAtRef.current = playCtxRef.current.currentTime;
        const an = playCtxRef.current.createAnalyser();
        an.fftSize = 1024; an.connect(playCtxRef.current.destination);
        outAnalyserRef.current = an;
      }
      const ctx = playCtxRef.current;
      const outBus = outAnalyserRef.current || ctx.destination;
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const n = Math.floor(bytes.byteLength / 4);
      if (n === 0) return;
      const buf = ctx.createBuffer(1, n, RATE_OUT);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < n; i++) ch[i] = Math.max(-1, Math.min(1, view.getFloat32(i * 4, true)));
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(outBus);
      src.onended = () => { playSourcesRef.current = playSourcesRef.current.filter((s) => s !== src); };
      playSourcesRef.current.push(src);
      const at = Math.max(playAtRef.current, ctx.currentTime);
      src.start(at); playAtRef.current = at + buf.duration;
    } catch { /* 忽略坏帧 */ }
  }, []);

  // 字幕分段更新：Start 起新段 / Response 替换文本 / End 定稿(空段丢弃)
  const upsertSeg = React.useCallback((setSegs, phaseKind, text) => {
    setSegs((prev) => {
      if (phaseKind === 'start') return [...prev, { id: newSegId(), text: '', done: false }];
      const arr = prev.slice();
      let i = arr.length - 1;
      while (i >= 0 && arr[i].done) i--;
      if (i < 0) { // 没有活跃段(漏了 start) → 起一段
        arr.push({ id: newSegId(), text: text || '', done: phaseKind === 'end' });
        return arr;
      }
      if (phaseKind === 'end' && !(text || '').trim()) { arr.splice(i, 1); return arr; } // 丢弃空段
      arr[i] = { ...arr[i], text: text != null ? text : arr[i].text, done: phaseKind === 'end' };
      return arr;
    });
  }, []);

  const handleFrame = React.useCallback((r) => {
    const E = Ast.AstEvent;
    // 错误状态码优先
    if (r.meta && !Ast.isOkStatus(r.meta.statusCode)) {
      setErrorMsg(`上游错误 code=${r.meta.statusCode} ${r.meta.message || ''}`);
      return;
    }
    switch (r.event) {
      case E.SessionStarted:
        setStatusBoth('ready'); setPhase('ready');
        startedAtRef.current = Date.now();
        stopTimer();
        timerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 250);
        startMic();
        break;
      case E.SourceSubtitleStart: upsertSeg(setSrcSegs, 'start'); setPhase('listening'); break;
      case E.SourceSubtitleResponse: upsertSeg(setSrcSegs, 'response', r.text); setPhase('listening'); break;
      case E.SourceSubtitleEnd: upsertSeg(setSrcSegs, 'end', r.text); break;
      case E.TranslationSubtitleStart: upsertSeg(setTgtSegs, 'start'); break;
      case E.TranslationSubtitleResponse: upsertSeg(setTgtSegs, 'response', r.text); break;
      case E.TranslationSubtitleEnd: upsertSeg(setTgtSegs, 'end', r.text); break;
      case E.TTSResponse:
        if (mode === 's2s' && r.audio) { playAudio(r.audio); setPhase('speaking'); }
        break;
      case E.TTSEnded:
        setPhase(statusRef.current === 'recording' ? 'listening' : 'ready');
        break;
      case E.UsageResponse: {
        const u = Ast.extractUsage(r.meta && r.meta.billing);
        setUsage((p) => ({
          inputAudio: p.inputAudio + u.inputAudio,
          outputText: p.outputText + u.outputText,
          outputAudio: p.outputAudio + u.outputAudio,
          durationMsec: p.durationMsec + u.durationMsec,
        }));
        break;
      }
      case E.SessionFailed:
        setErrorMsg((r.meta && r.meta.message) || 'SessionFailed');
        break;
      default: break;
    }
  }, [mode, playAudio, upsertSeg, stopTimer]);

  const startMic = async () => {
    if (micStreamRef.current) return;
    if (!isSecure()) { setErrorMsg('麦克风需要 HTTPS 或 localhost'); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } }); }
    catch (e) { setErrorMsg('无法访问麦克风: ' + (e && e.message ? e.message : e)); return; }
    micStreamRef.current = stream;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx(); micCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const inAn = ctx.createAnalyser(); inAn.fftSize = 1024;
    source.connect(inAn); micAnalyserRef.current = inAn;
    const proc = ctx.createScriptProcessor(4096, 1, 1); micNodeRef.current = proc;
    proc.onaudioprocess = (e) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || !sidRef.current) return;
      const pcm = floatToPcm16(e.inputBuffer.getChannelData(0), ctx.sampleRate);
      if (pcm.length) { try { ws.send(Ast.encodeAudio({ sessionId: sidRef.current, connectionId: cidRef.current, sequence: nextSeq(), pcm })); } catch {} }
    };
    source.connect(proc); proc.connect(ctx.destination);
    setStatusBoth('recording'); setPhase('listening');
  };

  const connect = async () => {
    if (sourceLang === targetLang) { setErrorMsg('源语言与目标语言不能相同'); return; }
    setErrorMsg(''); setSrcSegs([]); setTgtSegs([]); setElapsedMs(0);
    setUsage({ inputAudio: 0, outputText: 0, outputAudio: 0, durationMsec: 0 });
    sidRef.current = null; cidRef.current = null; seqRef.current = 0; finishedRef.current = false;
    setStatusBoth('connecting'); setPhase('connecting');

    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) { setStatusBoth('error'); setErrorMsg('ws-token mint 失败: ' + (e && e.message ? e.message : e)); return; }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
    let ws;
    try { ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token)); } catch (e) { setStatusBoth('error'); setErrorMsg('WebSocket 构造失败: ' + (e && e.message ? e.message : e)); return; }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    ws.onopen = () => {
      const uuid = (window.crypto && crypto.randomUUID) ? () => crypto.randomUUID() : () => `id-${Date.now()}-${Math.random()}`;
      sidRef.current = uuid(); cidRef.current = uuid();
      try {
        ws.send(Ast.encodeStartSession({
          sessionId: sidRef.current, connectionId: cidRef.current, sequence: nextSeq(),
          mode, sourceLanguage: sourceLang, targetLanguage: targetLang,
          inputRate: RATE_IN, targetFormat: 'pcm', targetRate: RATE_OUT,
        }));
      } catch (e) { setErrorMsg('StartSession 发送失败: ' + (e && e.message ? e.message : e)); }
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') return; // AST 全二进制
      try { handleFrame(Ast.decodeResponse(new Uint8Array(ev.data))); } catch { /* 忽略坏帧 */ }
    };
    ws.onerror = () => setErrorMsg((m) => m || 'WebSocket error');
    ws.onclose = (ev) => {
      teardown(false); wsRef.current = null; setPhase('idle'); stopTimer();
      if (statusRef.current !== 'error') setStatusBoth('closed');
      if (ev.code !== 1000 && ev.code !== 1005) setErrorMsg((m) => m || `Closed · code ${ev.code}${ev.reason ? ` · ${ev.reason}` : ''}`);
    };
  };

  const disconnect = () => { teardown(true); setStatusBoth('closed'); setPhase('idle'); };

  const live = status === 'ready' || status === 'recording' || status === 'connecting';
  const elapsedSec = elapsedMs / 1000;
  const estCost = perMinuteUsd > 0 ? (elapsedSec / 60) * perMinuteUsd : 0;
  const dot = status === 'recording' ? 'var(--err)' : status === 'ready' ? 'var(--ok, #35c98a)' : status === 'connecting' ? 'var(--c-amber, #f0b23a)' : status === 'error' ? 'var(--err)' : 'var(--fg-4)';
  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const transcript = React.useMemo(() => {
    const s = srcSegs.filter((x) => x.text.trim()).map((x) => `[${langLabel(sourceLang)}] ${x.text}`);
    const t = tgtSegs.filter((x) => x.text.trim()).map((x) => `[${langLabel(targetLang)}] ${x.text}`);
    return [...s, '', ...t].join('\n');
  }, [srcSegs, tgtSegs, sourceLang, targetLang]);

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      {/* 连接 + 语向 + 状态 + 用量 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">同声传译 · 豆包 AST 2.0（Seed LiveInterpret）</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
            <span style={{ color: 'var(--fg-3)' }}>{status}</span>
          </div>
        </div>
        <div style={{ padding: 14 }}>
          <div className="row gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {!live ? (
              <button className="btn btn-primary" onClick={connect} disabled={status === 'connecting'} style={{ height: 40, minWidth: 130 }}>
                {status === 'connecting' ? <><div className="spinner" style={{ width: 14, height: 14 }} /> 连接中…</> : <><XIcons.IconPlay size={14} /> 开始口译</>}
              </button>
            ) : (
              <button className="btn" onClick={disconnect} style={{ height: 40, minWidth: 130, borderColor: 'var(--err)', color: 'var(--err)' }}>
                <XIcons.IconPause size={14} /> 断开
              </button>
            )}
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)' }}>
              <select className="input" value={sourceLang} disabled={live} onChange={(e) => setSourceLang(e.target.value)} style={{ height: 32 }}>
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              <XIcons.IconArrowRight size={13} style={{ color: 'var(--fg-4)' }} />
              <select className="input" value={targetLang} disabled={live} onChange={(e) => setTargetLang(e.target.value)} style={{ height: 32 }}>
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)' }}>
              <span>模式</span>
              <select className="input" value={mode} disabled={live} onChange={(e) => setMode(e.target.value)} style={{ height: 32, maxWidth: 150 }}>
                <option value="s2s">语音+字幕</option>
                <option value="s2t">仅字幕</option>
              </select>
            </label>
          </div>
          <div className="row gap-3" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
            <span>时长 <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtTime(elapsedSec)}</span></span>
            {estCost > 0 && <span>估算(墙钟) <span className="mono" style={{ color: 'var(--fg-1)' }}>${estCost.toFixed(6)}</span></span>}
            {(usage.outputText > 0 || usage.outputAudio > 0) && (
              <span style={{ color: 'var(--fg-4)' }}>
                用量 · 译文 {usage.outputText} tok / 译音 {usage.outputAudio} tok / 音频 {(usage.durationMsec / 1000).toFixed(1)}s
              </span>
            )}
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)' }}>
          <XIcons.IconInfo size={13} /> <span style={{ overflowWrap: 'anywhere' }}>{errorMsg}</span>
        </div>
      )}

      {/* 云球 + 状态 */}
      <div className="card">
        <div className="col" style={{ alignItems: 'center', justifyContent: 'center', gap: 12, padding: '22px 16px 14px',
          background: 'radial-gradient(120% 80% at 50% 30%, rgba(80,120,220,0.06), transparent)' }}>
          <WsRealtimeOrb micAnalyserRef={micAnalyserRef} outAnalyserRef={outAnalyserRef} phase={live ? phase : 'idle'} size={160} />
          <div className="micro" style={{ color: 'var(--fg-3)', letterSpacing: 0.3 }}>{PHASE_LABEL[live ? phase : 'idle']}</div>
        </div>
      </div>

      {/* 双字幕 */}
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">字幕</div>
          {(srcSegs.length > 0 || tgtSegs.length > 0) && <XPrim.CopyBtn text={transcript} />}
        </div>
        <div className="row gap-3" style={{ padding: 14, flexWrap: 'wrap' }}>
          <SubtitlePanel title="原文" segs={srcSegs} lang={sourceLang} muted live={live} scrollRef={srcScrollRef} />
          <SubtitlePanel title="译文" segs={tgtSegs} lang={targetLang} muted={false} live={live} scrollRef={tgtScrollRef} />
        </div>
      </div>
    </div>
  );
}
